import { useEffect, isValidElement } from "react";

const ScrollSpy = ({
  className = "active",
  offsetTop = 0,
  offsetLeft = 0,
  position = "top",
  duration = 1000,
  children,
}) => {
  useEffect(() => {
    const sourceElements = [];
    const targetElements = [];
    let pendingScroll;

    const throttle = (fn, wait = 100) => {
      let timer, time = Date.now();

      return (params) => {
        clearTimeout(timer);

        if (time + wait - Date.now() < 0) {
          fn(params);
          time = Date.now();
        } else {
          timer = setTimeout(fn, wait / 5);
        }
      };
    };

    const onScrollHandler = throttle(() => {
      const scrollElement = document.scrollingElement || document.documentElement;

      const center = {
        x: scrollElement.scrollLeft + (position === "center" ? window.innerWidth / 2 : 0),
        y: scrollElement.scrollTop + (position === "center" ? window.innerHeight / 2 : 0),
      };

      sourceElements.map((source, i) => {
        const target = targetElements[i];

        const visibleHorizontal =
          target.offsetLeft >= 0 &&
          center.x >= target.offsetLeft &&
          center.x < target.offsetLeft + target.offsetWidth;

        const visibleVertical =
          target.offsetTop >= 0 &&
          center.y >= target.offsetTop - 1 &&
          center.y < target.offsetTop + target.offsetHeight - 2;

        if (visibleVertical && visibleHorizontal) {
          if (!source.classList.contains(className)) {
            scrollToMenu(source);
          }
          source.classList.add(className);
        } else {
          source.classList.remove(className);
        }

        return true;
      });

      if (sourceElements.length > 0 && !sourceElements.find(el => el.classList.contains(className))) {
        sourceElements[0].classList.add(className);
        scrollToMenu(sourceElements[0]);
      };
    });

    const onClickHandler = (event, targetTop, targetLeft, targetDuration) => {
      event.preventDefault();

      if (pendingScroll) return true;
      pendingScroll = true;

      const scrollElement = document.scrollingElement || document.documentElement;

      const scrollLimit = {
        x: Math.max(0, scrollElement.scrollWidth - window.innerWidth),
        y: Math.max(0, scrollElement.scrollHeight - window.innerHeight),
      };

      const scrollToPosition = {
        x: Math.min(Math.max(0, targetLeft + offsetLeft), scrollLimit.x),
        y: Math.min(Math.max(0, targetTop + offsetTop), scrollLimit.y),
      };

      const diff = {
        x: Math.round(scrollToPosition.x - scrollElement.scrollLeft),
        y: Math.round(scrollToPosition.y - scrollElement.scrollTop),
      };

      const step = {
        x: Math.round(((diff.x / Math.abs(targetDuration)) * 1000) / 16),
        y: Math.round(((diff.y / Math.abs(targetDuration)) * 1000) / 16),
      };

      const maxIterations = Math.max(Math.abs(step.x), Math.abs(step.y));
      let iterationCount = 0;

      const tick = (scrollTop, scrollLeft = 0) => {
        iterationCount++;

        const diffTick = {
          x: Math.round(scrollToPosition.x - scrollElement.scrollLeft),
          y: Math.round(scrollToPosition.y - scrollElement.scrollTop),
        };

        if ((diffTick.x === 0 && diffTick.y === 0) || iterationCount > maxIterations) {
          pendingScroll = false;
          return true;
        }

        if (Math.abs(diffTick.x) > 0) {
          scrollElement.scrollLeft += Math.abs(step.x) < Math.abs(diffTick.x) ? step.x : diffTick.x;
        }

        if (Math.abs(diffTick.y) > 0) {
          scrollElement.scrollTop += Math.abs(step.y) < Math.abs(diffTick.y) ? step.y : diffTick.y;
        }

        return requestAnimationFrame(() => tick(scrollTop, scrollLeft));
      };

      return tick(scrollToPosition);
    };

    const scrollToMenu = (el) => {
      el && (el.parentElement.scrollLeft = (el.offsetLeft + (el.offsetWidth / 2)) - (el.parentElement.offsetWidth / 2));
    }

    const getValidChildren = (children) => {
      const validChildren = [];
      children.forEach((child) => {
        if (!isValidElement(child)) {
          return;
        }

        if (child.type === "a") {
          validChildren.push(child);
        } else if (Array.isArray(child.props.children)) {
          validChildren.push(...getValidChildren(child.props.children));
        }
      });
      return validChildren;
    };

    getValidChildren(children).map((el) => {
      const href = el.props && el.props.href;
      const self = el.ref && el.ref.current;

      if (!self || !href) {
        return false;
      }

      const targetElement = href === "#" ? document.body : document.querySelector(href);
      if (targetElement) {
        self.onclick = (e) => onClickHandler(
          e,
          targetElement.offsetTop,
          targetElement.offsetLeft,
          duration
        );
        targetElements.push(targetElement);
        sourceElements.push(self);
      }

      return true;
    });

    if (targetElements.length) {
      const ScrollEvent = new Event("scroll");
      window.addEventListener("scroll", onScrollHandler, { passive: true });
      window.dispatchEvent(ScrollEvent);
    }

    return () => {
      window.removeEventListener("scroll", onScrollHandler);
    };
  }, [children, className, duration, position, offsetTop, offsetLeft]);

  return (
    <>{children}</>
  );
};

export default ScrollSpy;